import React, { useState } from 'react';
import { Tag, SkeletonText, InlineLoading } from 'carbon-components-react';
import { Information16 } from '@carbon/icons-react';
import { MapView } from 'components/EnquiryInformation/mapView';
import Utils from 'utilities/helper.util';
import dayjs from 'dayjs';
import { phoneNumberFormatter } from 'utilities/validation/phoneNumber.validation';
import { isNil } from 'lodash';

const EnquiryWebMap = ({
  enquiryDetail,
  setLualExtent
}: {
  enquiryDetail: any;
  setLualExtent: Function;
}) => {
  const [mapModalLoaded, setMapModalLoaded] = useState(false);

  return (
    <div className="mobile-view-job-details__show-info-location-map">
      {!mapModalLoaded && (
        <InlineLoading
          iconDescription="Loading"
          description="Loading map ..."
          style={{
            position: 'absolute',
            top: '50%',
            bottom: '50%',
            margin: 'auto',
            justifyContent: 'center',
            zIndex: 1
          }}
        />
      )}
      <MapView
        baseMapTheme={'light'}
        enquiryShape={enquiryDetail?.shape}
        onMapLoaded={() => setMapModalLoaded(true)}
        setLualExtent={setLualExtent}
      />
      <div className="mobile-view-job-details__map-overlay" />
    </div>
  );
};

const EnquiryValidity = ({
  enquiryValidityDate
}: {
  enquiryValidityDate: string;
}) => {
  return (
    <div>
      <Tag type="blue" className="mobile-view-job-details__validity-tag">
        <div className="mobile-view-job-details__validity-content">
          <Information16 /> This enquiry is valid until{' '}
          {Utils.toLocalDateFormat(enquiryValidityDate)}
        </div>
      </Tag>
    </div>
  );
};

const EnquiryAdditionalDetails = ({
  label,
  value,
  isLoading
}: {
  label: string;
  value?: React.ReactElement | string;
  isLoading: boolean;
}) => {
  return (
    <div className="mobile-view-job-details__additional-details">
      <div className="mobile-view-job-details__additional-details-label">
        {label}
      </div>
      <div>{isLoading ? <SkeletonText /> : value}</div>
    </div>
  );
};

// Dedupe the activity types and convert into string
function convertActivityArrIntoString(arr: string[]) {
  if (!arr || arr.length === 0) return '';

  const subject = arr[0].split(' - ')[0];
  const contentParts = arr.map((item) => {
    const content = item.split(' - ')[1];
    return content;
  });

  return `${subject}: ${contentParts.join(', ')}`;
}

function isEnquiryCreatedToday(createdAt: string) {
  const createdAtDate = dayjs(createdAt);
  const today = dayjs();
  return createdAtDate.isSame(today, 'day');
}

const formatEnquirerDetail = (enquirer: any) => {
  return (
    <React.Fragment>
      {enquirer.firstName} {enquirer.lastName} <br />
      {enquirer.companyName && (
        <React.Fragment>
          {enquirer.companyName}
          <br />
        </React.Fragment>
      )}
      {`${Utils.getFullAddress(enquirer.Address)}`} <br />
      {enquirer.email} <br />
      {!isNil(enquirer.phoneNumber) && (
        <React.Fragment>
          {phoneNumberFormatter({
            phoneNumber: enquirer.phoneNumber
          }).getNationalNumberFormat()}
          <br />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

function MobileMoreDetails({
  enquiryDetail,
  setLualExtent,
  enquiryValidityDate,
  isLoading
}: {
  enquiryDetail: any;
  setLualExtent: any;
  enquiryValidityDate: string;
  isLoading: boolean;
}) {
  return (
    <div className="mobile-view-job-details__background">
      <div className="mobile-view-job-details__content-container">
        <EnquiryWebMap
          enquiryDetail={enquiryDetail}
          setLualExtent={setLualExtent}
        />
        <EnquiryValidity enquiryValidityDate={enquiryValidityDate} />
        <EnquiryAdditionalDetails
          isLoading={isLoading || !enquiryDetail}
          label="Enquiry date"
          value={
            enquiryDetail && (
              <div>
                {enquiryDetail?.createdAt &&
                isEnquiryCreatedToday(enquiryDetail?.createdAt) ? (
                  <>
                    Today at {Utils.toLocalTimeFormat(enquiryDetail.createdAt)}
                  </>
                ) : (
                  <>
                    {Utils.toLocalDateFormat(enquiryDetail.createdAt)} at{' '}
                    {Utils.toLocalTimeFormat(enquiryDetail.createdAt)}
                  </>
                )}
              </div>
            )
          }
        />
        <EnquiryAdditionalDetails
          isLoading={isLoading || !enquiryDetail}
          label="Job site"
          value={enquiryDetail && Utils.getFullAddress(enquiryDetail.Address)}
        />
        <EnquiryAdditionalDetails
          isLoading={isLoading || !enquiryDetail}
          label="Activity"
          value={
            enquiryDetail &&
            convertActivityArrIntoString(enquiryDetail.activityTypes)
          }
        />
        <EnquiryAdditionalDetails
          isLoading={isLoading}
          label="Workplace location(s)"
          value={enquiryDetail && enquiryDetail.workplaceLocation}
        />
        <EnquiryAdditionalDetails
          isLoading={isLoading || !enquiryDetail}
          label="On behalf of"
          value={
            (enquiryDetail && enquiryDetail?.WorkingForAuthority?.name) ||
            enquiryDetail?.otherAuthorityName ||
            'Private'
          }
        />
        <EnquiryAdditionalDetails
          isLoading={isLoading || !enquiryDetail}
          label="Notes"
          value={enquiryDetail && (enquiryDetail?.notes || '-')}
        />
        <EnquiryAdditionalDetails
          isLoading={isLoading || !enquiryDetail}
          label="Enquirer"
          value={
            enquiryDetail &&
            enquiryDetail?.User &&
            formatEnquirerDetail(enquiryDetail.User)
          }
        />
      </div>
    </div>
  );
}
export default MobileMoreDetails;
